import { useState, useEffect, ReactNode } from 'react';
import { Box } from '@mui/material';

import { colors } from 'theme/mui/colors';

interface Props {
  children: ReactNode[];
  autoplay?: boolean;
  speed?: number;
  autoplaySpeed?: number;
  infinite?: boolean;
}

const ImageCarousel = (props: Props) => {
  const { children, autoplay = true, speed = 500, autoplaySpeed = 3000, infinite = true } = props;
  const count = children.length;
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!autoplay || count <= 1) return;
    const timer = setInterval(() => {
      setActiveStep(prev => {
        if (infinite) {
          return (prev + 1) % count;
        } else {
          if (prev < count - 1) {
            return prev + 1;
          } else {
            clearInterval(timer);
            return prev;
          }
        }
      });
    }, autoplaySpeed);
    return () => clearInterval(timer);
  }, [autoplay, autoplaySpeed, count, infinite]);

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          width: `${count * 100}%`,
          height: '100%',
          transform: `translateX(-${activeStep * (100 / count)}%)`,
          transition: `transform ${speed}ms ease-in-out`,
        }}
      >
        {children.map((slide, index) => (
          <Box key={index} sx={{ width: `${100 / count}%`, flexShrink: 0 }}>
            {slide}
          </Box>
        ))}
      </Box>

      {/* Indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
        }}
      >
        {children.map((_, idx) => (
          <Box
            key={idx + 1}
            onClick={() => setActiveStep(idx)}
            sx={{
              cursor: 'pointer',
              width: 30,
              height: 3,
              background: activeStep === idx ? colors.primary.main : 'white',
              borderRadius: 10,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ImageCarousel;
